import { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { subscribe } from '../lib/toast';

// Renderer for the global toast surface. Mount once at the top of the
// tree. Toast API lives in src/lib/toast.js so that file can be
// imported from anywhere without dragging a React component into the
// dep graph.

const TONE_STYLES = {
  success: {
    icon: CheckCircle,
    box:  'bg-[color:var(--color-ok)]/10 border-[color:var(--color-ok)]/30 text-ink-2',
    iconClass: 'text-ok'
  },
  error: {
    icon: AlertCircle,
    box:  'bg-[color:var(--color-err)]/10 border-[color:var(--color-err)]/30 text-ink-2',
    iconClass: 'text-err'
  },
  info: {
    icon: Info,
    box:  'bg-brand-soft border-[color:var(--color-brand)]/30 text-ink-2',
    iconClass: 'text-brand'
  }
};

export default function Toasts() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    return subscribe((t) => {
      if (t.dismiss) {
        setItems(prev => prev.filter(x => x.id !== t.dismiss));
        return;
      }
      setItems(prev => [...prev, t]);
      if (t.duration > 0) {
        setTimeout(() => {
          setItems(prev => prev.filter(x => x.id !== t.id));
        }, t.duration);
      }
    });
  }, []);

  const dismiss = (id) => setItems(prev => prev.filter(x => x.id !== id));

  if (items.length === 0) return null;

  return (
    <div
      // aria-live polite so a screen reader reads new toasts on its
      // own cadence without interrupting whatever the user was doing.
      aria-live="polite"
      aria-atomic="false"
      className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto pointer-events-none"
    >
      {items.map(t => {
        const cfg = TONE_STYLES[t.tone] || TONE_STYLES.info;
        const Icon = cfg.icon;
        return (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-2xl border shadow-lg animate-in slide-in-from-right-2 fade-in duration-200 ${cfg.box}`}
          >
            <Icon size={18} className={`mt-0.5 shrink-0 ${cfg.iconClass}`} aria-hidden="true" />
            <div className="flex-1 text-sm font-medium leading-relaxed">
              {t.message}
              {t.action && (
                <button
                  type="button"
                  onClick={() => { dismiss(t.id); t.action.onClick(); }}
                  className="block mt-1.5 text-xs font-bold text-brand hover:underline"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Schließen"
              className="p-1 -m-1 opacity-60 hover:opacity-100 transition-opacity shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
